/**
 * Nachrichtenverlauf eines Tickets (Spec 7.2).
 *
 * Die drei Arten sind farblich getrennt — die interne Notiz deutlich abgesetzt,
 * damit sie niemand mit einer Kundenantwort verwechselt.
 *
 * `body_html` kommt serverseitig gerendert **und** sanitisiert aus der API
 * (`services/richtext.ts`); nur deshalb ist `dangerouslySetInnerHTML` hier
 * vertretbar (security.md: „nur fuer sanitizierten Rich-Text-Output").
 */
import type { TicketMessage } from '@tickethub/shared';
import { Lock, MailOpen, Reply } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const STYLES: Record<TicketMessage['kind'], { box: string; icon: typeof Reply }> = {
  inbound: { box: 'border-border bg-card', icon: MailOpen },
  outbound: { box: 'border-primary/30 bg-primary/5', icon: Reply },
  internal_note: { box: 'border-amber-400/60 bg-amber-50 dark:bg-amber-950/30', icon: Lock },
};

export function MessageThread({
  messages,
  locale,
}: {
  messages: TicketMessage[];
  locale: string;
}) {
  const { t } = useTranslation();
  const formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  if (messages.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('ticket.no_messages')}</p>;
  }

  return (
    <ol className="space-y-4">
      {messages.map((message) => {
        const style = STYLES[message.kind];
        const Icon = style.icon;

        return (
          <li key={message.id} className={cn('rounded-lg border p-4', style.box)}>
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Icon aria-hidden="true" className="size-4" />
              <Badge variant={message.kind === 'internal_note' ? 'outline' : 'secondary'}>
                {t(`ticket.kind_${message.kind}`)}
              </Badge>
              {message.external_author_name ? (
                // ADR 0018: Bearbeiter-Name aus der Quell-App — kein Mitglied.
                <span className="font-medium text-foreground">{message.external_author_name}</span>
              ) : null}
              <time dateTime={message.created_at}>
                {formatter.format(new Date(message.created_at))}
              </time>
              {message.delivery_status ? (
                <Badge variant={message.delivery_status === 'failed' ? 'destructive' : 'outline'}>
                  {t(`ticket.delivery_${message.delivery_status}`)}
                </Badge>
              ) : null}
            </div>

            <div
              className="prose-sm max-w-none text-sm [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5"
              // `body_html` ist serverseitig gerendert und sanitisiert (ADR-Kette
              // shared/richtext.ts → services/richtext.ts) — nie Client-Eingabe.
              dangerouslySetInnerHTML={{ __html: message.body_html }}
            />

            {message.attachments.length > 0 ? (
              <ul className="mt-3 flex flex-wrap gap-2">
                {message.attachments.map((attachment) => (
                  <li key={attachment.id}>
                    <a
                      className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-accent"
                      href={attachment.download_url}
                      download={attachment.filename}
                    >
                      {attachment.filename}
                      <span className="text-muted-foreground">
                        ({Math.max(1, Math.round(attachment.size / 1024))} KB)
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
